import React, { useState, useEffect } from 'react';
import { 
    Dialog, DialogTitle, DialogContent, DialogActions, 
    Button, Typography, TextField, Autocomplete, 
    Stack, IconButton, MenuItem, Grid, ToggleButtonGroup, ToggleButton
} from '@mui/material';
import { MdClose } from "react-icons/md";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/de';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../../App.js';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Europe/Berlin");

const timeOptions = [];
for (let h = 8; h < 24; h++) {
    timeOptions.push(`${String(h).padStart(2, '0')}:00`);
}

const AdminBookingModal = ({ open, onClose, players = [] }) => {
    const [loading, setLoading] = useState(false);
    const [activeCourts, setActiveCourts] = useState([1, 2, 3, 4, 5, 6]);
    
    const [formData, setFormData] = useState({
        playerId: null,
        courtId: 1,
        date: dayjs.tz(),
        startTime: '10:00',
        gameType: 'single',
        partners: [null]
    });

    useEffect(() => {
        if (open) {
            setFormData({
                playerId: null,
                courtId: activeCourts[0] || 1,
                date: dayjs.tz(),
                startTime: '10:00',
                gameType: 'single',
                partners: [null]
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (open && formData.date) {
            const fetchCourts = async () => {
                try {
                    const formattedDate = formData.date.format('YYYY-MM-DD');
                    const courtRes = await axios.get(`${API_BASE_URL}/api/courts?date=${formattedDate}`);
                    let courtsList = [];
                    if (courtRes.data && courtRes.data.length > 0) {
                        courtsList = courtRes.data.filter(c => c.isActive !== false).map(c => c.courtNumber);
                    }
                    if (courtsList.length === 0) {
                        courtsList = [1, 2, 3, 4, 5, 6];
                    }
                    setActiveCourts(courtsList);
                    
                    setFormData(prev => {
                        if (!courtsList.includes(prev.courtId)) {
                            return { ...prev, courtId: courtsList.length > 0 ? courtsList[0] : 1 };
                        }
                        return prev;
                    });
                } catch (err) {
                    console.error("Error fetching courts:", err);
                }
            };
            fetchCourts();
        }
    }, [formData.date, open]);

    const handleGameTypeChange = (e, val) => {
        if (!val) return;
        setFormData(prev => ({
            ...prev,
            gameType: val,
            partners: Array(val === 'single' ? 1 : 3).fill(null)
        }));
    };

    const handleSubmit = async () => {
        if (!formData.playerId) return toast.error("Bitte wähle einen Hauptspieler aus.");
        if (formData.partners.some(p => !p)) return toast.error("Bitte alle Mitspieler auswählen.");

        const start = dayjs.tz(formData.date).hour(parseInt(formData.startTime.split(':')[0])).minute(0).second(0).millisecond(0);
        
        if (start.isBefore(dayjs.tz())) {
            return toast.error("Buchungen in der Vergangenheit sind nicht erlaubt.");
        }

        const duration = formData.gameType === 'single' ? 1 : 2;
        
        const booking = {
            mainPlayer: formData.playerId.id, // The selected player will receive the invoice for guests
            playerId: formData.playerId.id,
            courtId: formData.courtId,
            startTime: start.toISOString(),
            endTime: start.add(duration, 'hour').toISOString(),
            gameType: formData.gameType,
            partners: formData.partners.map(p => p.id)
        };

        setLoading(true);
        try {
            const res = await axios.post(`${API_BASE_URL}/api/booking`, booking);
            toast.success(res.data.message || "Buchung erfolgreich erstellt");
            onClose(true);
        } catch (error) {
            toast.error(error.response?.data?.message || "Fehler bei der Buchung");
        } finally {
            setLoading(false);
        }
    };

    const getAvailablePartners = (index) => {
        const selectedIds = [
            formData.playerId?.id,
            ...formData.partners.filter((p, i) => p && i !== index).map(p => p.id)
        ].filter(Boolean);
        
        return players.filter(p => !selectedIds.includes(p.id));
    };

    return (
        <Dialog open={open} onClose={() => onClose()} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Neue Buchung (Admin)
                <IconButton onClick={() => onClose()} size="small"><MdClose /></IconButton>
            </DialogTitle>
            
            <DialogContent dividers>
                <Stack spacing={3} sx={{ mt: 1 }}>
                    <Autocomplete
                        options={players}
                        getOptionLabel={(option) => option.name || ""}
                        value={formData.playerId}
                        onChange={(e, val) => setFormData(prev => ({ ...prev, playerId: val }))}
                        renderInput={(params) => <TextField {...params} label="Hauptspieler / Bucher" required />}
                    />

                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <DatePicker 
                                label="Datum"
                                value={formData.date}
                                onChange={(val) => setFormData(prev => ({ ...prev, date: val }))}
                                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                disablePast
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                select
                                label="Startzeit"
                                value={formData.startTime}
                                onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
                                fullWidth
                                size="small"
                            >
                                {timeOptions.map(t => <MenuItem key={t} value={t}>{t} Uhr</MenuItem>)}
                            </TextField>
                        </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextField
                                select
                                label="Platz"
                                value={formData.courtId || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, courtId: e.target.value }))}
                                fullWidth
                                size="small"
                            >
                                {activeCourts.map(n => <MenuItem key={n} value={n}>Platz {n}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <ToggleButtonGroup
                                value={formData.gameType}
                                exclusive
                                onChange={handleGameTypeChange}
                                fullWidth
                                size="small"
                                color="primary"
                            >
                                <ToggleButton value="single">Einzel</ToggleButton>
                                <ToggleButton value="double">Doppel</ToggleButton>
                            </ToggleButtonGroup>
                        </Grid>
                    </Grid>

                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                        MITSPIELER
                    </Typography>
                    
                    <Stack spacing={2}>
                        {formData.partners.map((partner, index) => (
                            <Autocomplete
                                key={index}
                                options={getAvailablePartners(index)}
                                getOptionLabel={(option) => option.name || ""}
                                value={partner}
                                onChange={(e, val) => {
                                    const newPartners = [...formData.partners];
                                    newPartners[index] = val;
                                    setFormData(prev => ({ ...prev, partners: newPartners }));
                                }}
                                renderInput={(params) => <TextField {...params} label={`Partner ${index + 1}`} required />}
                                size="small"
                            />
                        ))}
                    </Stack>
                </Stack>
            </DialogContent>

            <DialogActions sx={{ p: 2, bgcolor: '#f8f9fa' }}>
                <Button onClick={() => onClose()}>Abbrechen</Button>
                <Button 
                    variant="contained" 
                    onClick={handleSubmit} 
                    disabled={loading}
                    sx={{ fontWeight: 'bold' }}
                >
                    {loading ? "Wird gebucht..." : "Buchung erstellen"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AdminBookingModal;
